import { ReactNode, useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { Store, Activity,
  LayoutDashboard, Users, ShieldCheck, LifeBuoy, Banknote, Landmark, LineChart,
  Link2, FileText, Megaphone, Coins, Settings2, ScrollText, UserCog, ArrowRight, Wallet,
  Menu, X, ExternalLink, MessageSquare, Scale, BookOpen, Vault, Mail,
} from 'lucide-react'
import { clsx, fa } from '../lib/format'
import { useAuth } from '../store'

export const NAV: { group: string; items: { to: string; label: string; icon: any; perm?: string }[] }[] = [
  {
    group: 'نمای کلی',
    items: [{ to: '/admin', label: 'داشبورد', icon: LayoutDashboard }],
  },
  {
    group: 'کاربران',
    items: [
      { to: '/admin/users', label: 'مدیریت کاربران', icon: Users, perm: 'users.read' },
      { to: '/admin/kyc', label: 'احراز هویت', icon: ShieldCheck, perm: 'kyc.read' },
      { to: '/admin/tickets', label: 'تیکت پشتیبانی', icon: LifeBuoy, perm: 'support.write' },
    ],
  },
  {
    group: 'مالی',
    items: [
      { to: '/admin/finance', label: 'واریز و برداشت', icon: Banknote, perm: 'finance.read' },
      { to: '/admin/commerce', label: 'درگاه پذیرندگی', icon: Store, perm: 'finance.read' },
      { to: '/admin/wallets', label: 'کیف پول‌ها و دارایی‌ها', icon: Wallet, perm: 'chain.read' },
      { to: '/admin/treasury-status', label: 'وضعیت خزانه ارزینو', icon: Vault, perm: 'finance.read' },
      { to: '/admin/treasury', label: 'خزانه و درآمد', icon: Landmark, perm: 'finance.read' },
      { to: '/admin/accounting', label: 'حسابداری رمزارز', icon: Scale, perm: 'finance.read' },
      { to: '/admin/ledger', label: 'دفتر کل دارایی', icon: BookOpen, perm: 'finance.read' },
    ],
  },
  {
    group: 'بازار',
    items: [
      { to: '/admin/trading', label: 'سفارش‌ها و معاملات', icon: LineChart, perm: 'trading.read' },
      { to: '/admin/chain', label: 'شبکه‌های بلاک‌چین', icon: Link2, perm: 'chain.read' },
      { to: '/admin/staking', label: 'استیکینگ', icon: Coins, perm: 'staking.write' },
    ],
  },
  {
    group: 'محتوا',
    items: [
      { to: '/admin/content', label: 'وبلاگ', icon: FileText, perm: 'content.write' },
      { to: '/admin/pulse', label: 'نبض بازار و تقویم', icon: Activity, perm: 'content.write' },
      { to: '/admin/announcements', label: 'اطلاعیه و پیام همگانی', icon: Megaphone, perm: 'announce.write' },
      { to: '/admin/sms', label: 'سامانه پیامک', icon: MessageSquare, perm: 'sms.read' },
      { to: '/admin/email', label: 'سامانه ایمیل', icon: Mail, perm: 'sms.read' },
    ],
  },
  {
    group: 'سامانه',
    items: [
      { to: '/admin/settings', label: 'تنظیمات سامانه', icon: Settings2, perm: 'settings.read' },
      { to: '/admin/staff', label: 'کارکنان و نقش‌ها', icon: UserCog, perm: 'users.read' },
      { to: '/admin/logs', label: 'گزارش اقدامات', icon: ScrollText, perm: 'logs.read' },
    ],
  },
]

export function AdminLayout({ children, perms = [] }: { children: ReactNode; perms?: string[] }) {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const loc = useLocation()
  useEffect(() => setOpen(false), [loc.pathname])

  const can = (perm?: string) => !perm || perms.includes('*') || perms.includes(perm)

  return (
    <div className="min-h-screen bg-ink-950">
      {/* ---------------- نوار بالا ---------------- */}
      <header className="sticky top-0 z-40 border-b border-white/[.07] bg-ink-950/90 backdrop-blur-2xl">
        <div className="flex h-14 items-center gap-3 px-4">
          <button className="btn-ghost !px-2 lg:hidden" onClick={() => setOpen((o) => !o)}>
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <Link to="/admin" className="flex items-center gap-2.5">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br from-brand-500 to-aqua text-sm font-black text-white">
              A
            </span>
            <span className="font-extrabold text-white">ارزینو</span>
            <span className="chip !py-0.5 text-[10px] text-brand-200">پنل مدیریت</span>
          </Link>

          <div className="mr-auto flex items-center gap-2">
            <Link to="/" className="btn-ghost !px-3 text-xs">
              <ExternalLink className="h-3.5 w-3.5" /> <span className="hidden sm:inline">مشاهده سایت</span>
            </Link>
            <div className="hidden items-center gap-2 rounded-xl border border-white/10 bg-white/[.04] px-3 py-1.5 sm:flex">
              <span className="grid h-6 w-6 place-items-center rounded-full bg-brand-500/20 text-[10px] font-bold text-brand-200">
                {(user?.email ?? '?').slice(0, 2).toUpperCase()}
              </span>
              <div className="leading-tight">
                <div className="max-w-[10rem] truncate text-[11px] text-slate-200">{user?.email}</div>
                <div className="text-[10px] text-brand-300">{(user as any)?.roleLabel ?? 'مدیر'}</div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* ---------------- کناره ---------------- */}
        <aside className={clsx(
          'fixed inset-y-0 right-0 z-50 w-64 shrink-0 overflow-y-auto border-l border-white/[.07] bg-ink-900/97 p-3 pt-4 backdrop-blur-2xl transition-transform lg:sticky lg:top-14 lg:z-0 lg:h-[calc(100vh-3.5rem)] lg:translate-x-0 lg:bg-transparent lg:backdrop-blur-none',
          open ? 'translate-x-0' : 'translate-x-full',
        )}>
          <div className="mb-3 flex items-center justify-between lg:hidden">
            <span className="text-sm font-bold text-white">منوی مدیریت</span>
            <button onClick={() => setOpen(false)} className="rounded-lg p-1.5 text-slate-400 hover:bg-white/10">
              <X className="h-4 w-4" />
            </button>
          </div>

          {NAV.map((g) => {
            const items = g.items.filter((i) => can(i.perm))
            if (!items.length) return null
            return (
              <div key={g.group} className="mb-4">
                <div className="mb-1.5 px-3 text-[10px] font-medium text-slate-600">{g.group}</div>
                {items.map((i) => (
                  <NavLink key={i.to} to={i.to} end={i.to === '/admin'}
                    className={({ isActive }) => clsx(
                      'mb-0.5 flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-[13px] transition',
                      isActive
                        ? 'bg-gradient-to-l from-brand-600/30 to-transparent text-white ring-1 ring-brand-500/30'
                        : 'text-slate-400 hover:bg-white/[.05] hover:text-slate-100',
                    )}>
                    <i.icon className="h-4 w-4" />
                    {i.label}
                  </NavLink>
                ))}
              </div>
            )
          })}

          <Link to="/dashboard" className="mt-2 flex items-center gap-2 rounded-xl px-3 py-2.5 text-[13px] text-slate-500 hover:bg-white/[.05]">
            <ArrowRight className="h-4 w-4" /> بازگشت به پنل کاربری
          </Link>
        </aside>

        {open && <div className="fixed inset-0 z-40 bg-ink-950/70 lg:hidden" onClick={() => setOpen(false)} />}

        {/* ---------------- محتوا ---------------- */}
        <main className="min-w-0 flex-1 p-3 sm:p-6">{children}</main>
      </div>
    </div>
  )
}

export function PageHead({ title, subtitle, action }:
  { title: string; subtitle?: string; action?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-extrabold text-white sm:text-2xl">{title}</h1>
        {subtitle && <p className="mt-1 text-xs text-slate-400">{subtitle}</p>}
      </div>
      {action}
    </div>
  )
}
